/**
 * @author Akash Mishra <akashmishra@eurofins.com>
 */
import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClindxTableFilterConfig, ClindxTableFilterType } from 'src/app/core/classes/clindx-table-config';
import { SearchFilter } from 'src/app/core/interfaces/search-filter';
import { DateRangeComponent } from 'src/app/shared/date-range/date-range.component';
import { isNullOrUndefined, isNumber } from 'util';

import { Constants, UserActivityLog } from '../constants';
import { DatePicker } from '../error-messages';
import { BroadcastService } from '../services/broadcast.service';
import { PhysicianService } from '../services/physician.service';

@Component({
    selector: 'app-clindx-table-filter',
    templateUrl: './clindx-table-filter.component.html',
    styleUrls: ['./clindx-table-filter.component.scss'],
    standalone: false
})
export class ClindxTableFilterComponent implements OnInit, AfterViewInit {

  isScreenSmall: Observable<boolean>;
  isValueSelected = true;
  ClindxTableFilterType = ClindxTableFilterType;
  selectedFilter = new ClindxTableFilterConfig();
  dateRangeForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null)
  });
  public labelName: string = null;
  public labelValue: string;
  public filterValue: any = '';
  public filterCriterias = new Array<ClindxTableFilterConfig>();
  public minDate: moment.Moment = moment('1900-01-01');
  public maxDate: moment.Moment = moment('2100-12-31');
  public maxDateForDOB: Date = new Date();
  public minDateforDob: Date = new Date('1900-01-01');
  public filterInfoMessage = UserActivityLog.ActivityDetailsFilterMessage;
  isNpiDialog: boolean;
  rangesFooter = DateRangeComponent;
  ValidationMessages = {
    MaxDateValidation: DatePicker.GREATER_DATE,
    Invaliddatevalidation: DatePicker.INVALID_DATE
  };

  @ViewChild('reportedOnRangePicker') reportedOnRangePicker: MatDateRangePicker<Date>;
  @ViewChild('dateRangePicker') dateRangePicker: MatDateRangePicker<Date>;

  constructor(
    public dialogRef: MatDialogRef<ClindxTableFilterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClindxTableFilterConfig[],
    public broadcastService: BroadcastService,
    breakpoints: BreakpointObserver,
    private physicianService: PhysicianService) {

    this.isScreenSmall = breakpoints.observe(`(max-width: ${Constants.SMALL_WIDTH_BREAKPOINT}px)`)
      .pipe(map(breakpoint => breakpoint.matches));
    this.filterCriterias = data.filter(item => item.columnName !== 'resultDetails');
    this.isNpiDialog = this.physicianService.npiDialogStatus;
  }

  ngAfterViewInit(): void {
  if (window.innerWidth > Constants.EXTRA_SMALL_WIDTH_BREAKPOINT && this.isNpiDialog) {
    const domRefHTMLcollection = document.getElementsByClassName('mat-table-filter__icon');
    const lastElement = domRefHTMLcollection[domRefHTMLcollection.length - 1];
    if (!lastElement) { return; }
    const domRec = lastElement.getBoundingClientRect();  //fix for a console error coming in npi dialog box on opening filter
    this.dialogRef.updatePosition({ left: `${domRec.left}px`,
    top: `${domRec.top + 40}px`});
    }
   }

  closeDialog() {
    this.dialogRef.close(false);
  }

  ngOnInit(): void{
    // can perform any logical implemention here if required
  }

  onDateChanged() {
    const start = this.dateRangeForm.get('start')?.value;
    const end = this.dateRangeForm.get('end')?.value;
    this.isValueSelected = !(start && end);
  }

  onPickerClosed() {
    const start = this.dateRangeForm.get('start')?.value;
    const end = this.dateRangeForm.get('end')?.value;
    if (start && !end) {
      this.dateRangeForm.patchValue({ end: start }, { emitEvent: false });
    }
    const updatedStart = this.dateRangeForm.get('start')?.value;
    const updatedEnd = this.dateRangeForm.get('end')?.value;
    this.isValueSelected = !(updatedStart && updatedEnd);
  }

  checkValue(event) {
    if (isNumber(event)) {
      event = event.toString();
    }
    if (isNullOrUndefined(event)) {
      this.isValueSelected = false;
    } else if (event.status) {
      this.isValueSelected = event.status === 'INVALID';
    } else if (this.selectedFilter.type === ClindxTableFilterType.inputTypeWithMinLength) {
      this.isValueSelected = event.length > this.selectedFilter.minLength ? false : true;
    } else {
      this.isValueSelected = event.length > 0 ? false : true;
    }
  }

  getValue(selected: ClindxTableFilterConfig) {
    this.isValueSelected = false;
    this.labelName = selected.columnDisplayValue || selected.name;
    this.labelValue = selected.columnName || selected.id;
    this.isValueSelected = true;
    this.selectedFilter = selected;
    this.filterValue = '';
    this.dateRangeForm.reset();
  }

  applyFilter() {
    if (this.selectedFilter.type === ClindxTableFilterType.dateRangeType || this.selectedFilter.type === ClindxTableFilterType.dateType) {
      this.applyDateRangeFilter();
    } else if (this.selectedFilter.type === ClindxTableFilterType.dateWithValidationType) {
      this.applySingleDateFilter();
    } else if (this.labelValue !== '' && this.filterValue !== '') {
      this.applyStandardFilter();
    }
    this.dialogRef.close(false);
  }

  private applyDateRangeFilter(): void {
    const start = this.dateRangeForm.get('start')?.value;
    const end = this.dateRangeForm.get('end')?.value;
    if (this.labelValue !== '' && start && end) {
      const b = this.normalizeToDate(start);
      const e = this.normalizeToDate(end);
      if (b && e) {
        const searchFilter: SearchFilter = {
          selectedFilterLabelName: this.labelName,
          selectedFilterLabelValue: this.labelValue,
          filterType: this.selectedFilter.type,
          filterText: { begin: b, end: e }
        };
        this.broadcastFilter(searchFilter);
      }
    }
  }

  private applySingleDateFilter(): void {
    if (this.labelValue !== '' && this.filterValue) {
      const dateValue = this.filterValue instanceof Date ? this.filterValue : new Date(this.filterValue);
      if (!isNaN(dateValue.getTime())) {
        const searchFilter: SearchFilter = {
          selectedFilterLabelName: this.labelName,
          selectedFilterLabelValue: this.labelValue,
          filterType: this.selectedFilter.type,
          filterText: dateValue as any
        };
        this.broadcastFilter(searchFilter);
      }
    }
  }

  private applyStandardFilter(): void {
    const searchFilter: SearchFilter = {
      selectedFilterLabelName: this.labelName,
      selectedFilterLabelValue: this.labelValue,
      filterType: this.selectedFilter.type,
      filterText: (this.selectedFilter.type === ClindxTableFilterType.enumType && this.selectedFilter.isMulti === true) ?
        this.filterValue : this.filterValue.toString().trim()
    };
    this.broadcastFilter(searchFilter);
  }

  private broadcastFilter(searchFilter: SearchFilter): void {
    this.broadcastService.broadcast((this.isNpiDialog) ? Constants.NPI_FILTER_EVENT : Constants.FILTER_EVENT, searchFilter);
  }

  onQuickRangeSelected(range: {begin: Date, end: Date}) {
    this.dateRangeForm.patchValue({
      start: this.normalizeToDate(range.begin),
      end: this.normalizeToDate(range.end)
    });
    // Enable apply button and close picker
    this.isValueSelected = false;
    
    setTimeout(() => {
      if (this.selectedFilter.type === ClindxTableFilterType.dateRangeType && this.reportedOnRangePicker) {
        this.reportedOnRangePicker.close();
      } else if (this.selectedFilter.type === ClindxTableFilterType.dateType && this.dateRangePicker) {
        this.dateRangePicker.close();
      }
    }, 100);
  }

  private normalizeToDate(v: any): Date | null {
    if (!v && v !== 0) { return null; }
    if (v instanceof Date) { return v; }
    if (v && typeof v.toDate === 'function') {
      try { return v.toDate(); } catch (e) { /* fallthrough */ }
    }
    if (v && v._d instanceof Date) { return v._d; }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  applyDisabilityCheck() {
    return this.isValueSelected;
  }
}
